import React, { useState, useEffect, useMemo } from 'react';
import {
  fetchAllWritingExercises,
  updateWritingExercise,
  deleteWritingExercise
} from '../../services/api';
import Loading from '../Loading';
import Error from '../Error';
import AdminTable from './AdminTable';
import { grammarCategoriesField } from './GrammarCategoriesField';

// examples se edituje jako text: jeden příklad na řádek ve tvaru "česky | anglicky"
const serializeExamples = (value) => {
  if (typeof value !== 'string') {
    return value || [];
  }
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [czech, english] = line.split('|').map((s) => (s || '').trim());
      return { czech: czech || '', english: english || '' };
    });
};

const deserializeExamples = (value) => {
  if (!Array.isArray(value)) {
    return value || '';
  }
  return value.map((ex) => `${ex.czech || ''} | ${ex.english || ''}`).join('\n');
};

const WritingExerciseManager = () => {
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchExercises = async () => {
    try {
      setLoading(true);
      const response = await fetchAllWritingExercises();
      setExercises(response.exercises || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching writing exercises:', err);
      setError('Failed to load writing exercises');
      setExercises([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExercises();
  }, []);

  const uniqueTopics = useMemo(() => {
    return [...new Set(exercises.map((item) => item.topic))].filter(Boolean);
  }, [exercises]);

  const handleSave = async (id, data) => {
    await updateWritingExercise(id, data);
    setExercises((prev) => prev.map((ex) => (ex.id === id ? { ...ex, ...data } : ex)));
  };

  const handleDelete = async (id) => {
    await deleteWritingExercise(id);
    setExercises((prev) => prev.filter((ex) => ex.id !== id));
  };

  const columns = [
    { key: 'id', header: 'ID', width: 60 },
    { key: 'topic', header: 'Topic', editable: true, width: 120 },
    { key: 'exercise_order', header: 'Order', width: 70 },
    { key: 'title', header: 'Title', editable: true, width: 150 },
    { key: 'instruction', header: 'Instruction', editable: true, width: 250 },
    { key: 'min_sentences', header: 'Min Sentences', editable: true, width: 90 },
    { key: 'examples', header: 'Examples', editable: true, width: 220,
      render: (value) => Array.isArray(value)
        ? value.map((ex) => `${ex.czech || ''} | ${ex.english || ''}`).join('; ')
        : (value || ''),
    },
    { key: 'grammar_categories', header: 'Grammar Categories', editable: true, width: 150,
      render: (value) => Array.isArray(value) ? value.join(', ') : (value || ''),
    },
    {
      key: 'created_at',
      header: 'Created',
      width: 100,
      render: (value) => (value ? new Date(value).toLocaleDateString() : '-'),
    },
  ];

  const editableFields = [
    { key: 'topic', type: 'select', label: 'Topic', required: true, options: uniqueTopics },
    { key: 'title', type: 'text', label: 'Title', required: true },
    { key: 'instruction', type: 'textarea', label: 'Instruction', minHeight: '80px', required: true },
    { key: 'min_sentences', type: 'number', label: 'Min Sentences', default: 5 },
    { key: 'examples', type: 'textarea', label: 'Examples (jeden na řádek: česky | anglicky)', minHeight: '80px',
      deserialize: deserializeExamples,
      serialize: serializeExamples,
    },
    grammarCategoriesField,
  ];

  if (loading && exercises.length === 0) {
    return <Loading />;
  }

  if (error && exercises.length === 0) {
    return <Error message={error} />;
  }

  return (
    <AdminTable
      data={exercises}
      columns={columns}
      onSave={handleSave}
      onDelete={handleDelete}
      editableFields={editableFields}
      title="Psaní"
      filterField="topic"
      uniqueFilterValues={uniqueTopics}
      loading={loading}
      emptyMessage="No writing exercises found."
    />
  );
};

export default WritingExerciseManager;
